// ─────────────────────────────────────────────────────────────────────────────
// TrucoGameTableReducer.js
// ─────────────────────────────────────────────────────────────────────────────

/** A clean per-hand trick-win record: one slot per trick (max 3), null = not yet played */
const emptyTrickWins = () => [null, null, null]

export const initialTrucoGameState = {
    myPlayerId: sessionStorage.getItem("playerId"),

    // match lifecycle
    phase: "IDLE",

    match: {
        handNumber: 1,
        isGameOver: false
    },

    // table state
    table: {},
    seats: [],
    playerDetails: [],
    trickCount: 1,
    dealerIndex: null,
    handIndex: null,
    gameDirection: 1,
    centerCards: [],

    // score (server authoritative)
    points: {
        myPoints: 0,
        playingPoints: 1,
        opponentPoints: 0
    },

    timer: {
        active: false,
        seatNumber: null,
        duration: 15,
        remaining: 0,
        startedAt: null
    },

    turn: {
        currentSeat: null,
        dealerSeat: null
    },

    action: {
        phase: "CARD_PLAY",
        canPlayCard: false,
        serverCanDraw: false
    },

    bet: {
        phase: "NONE",
        trucoLevel: 0,
        envidoLevel: 0,
        pendingFrom: null,
        envidoChain: []
    },

    playerActions: [],
    isCenterCardsStack: true,
    envidoDetails: null,
    showEnvidoDetails: false,
    gameWinnerDetails: null,
    showGameWinDetails: false,
    showRestartGameOptions: false,
    /**
     * symmetricOpponents (configurable)
     * ─────────────────────────────────
     * When true every non-local seat is rendered with a scaleX(-1) CSS
     * transform so its layout mirrors the local player's seat, creating a
     * symmetric table appearance.  Toggle at runtime via SET_SYMMETRIC_OPPONENTS.
    */
    symmetricOpponents: false,
    /**
    * settings
    * ─────────────────────────────────
    * User-configurable UI preferences that persist across hands/resets.
    *   isSettingsOpen   — whether the settings dialog is currently open
    *   notifPosition    — where toast notifications appear on screen
    *   activeTheme      — current colour theme key for the table
    */
    settings: {
        isSettingsOpen: false,
        notifPosition: "center",
        activeTheme: "crimson",
    },
}


// ── helpers ──────────────────────────────────────────────────────────────────

const getInitialActionState = () => ({
    phase: "CARD_PLAY",
    canPlayCard: false
})

const getInitialBetState = () => ({
    phase: "NONE",
    trucoLevel: 0,
    envidoLevel: 0,
    pendingFrom: null,
    envidoChain: []
})

const getInitialHandState = () => ({
    trickCount: 1,
    action: getInitialActionState(),
    bet: getInitialBetState(),
    centerCards: []
})

const getNextTrucoLevel = level => Math.min(level + 1, 3)

// ── reducer ───────────────────────────────────────────────────────────────────

export function trucoGameReducer(state, action) {
    switch (action.type) {
        case "SET_PLAYER_NAME":
            return { ...state, myPlayerId: action.payload }
        // ── lifecycle ────────────────────────────────────────────────────────
        case "SET_PHASE":
            return { ...state, phase: action.payload }

        case "NEXT_HAND":
            return {
                ...state,
                match: { ...state.match, handNumber: state.match.handNumber + 1 }
            }

        case "GAME_COMPLETED":
            return {
                ...state,
                seats: state.seats.map(seat => ({ ...seat, cards: [] })),
                match: { ...state.match, isGameOver: true }
            }
        /**
         * RESET_GAME
         * Wipes all game state back to initialTrucoGameState.
         * Preserves only `myPlayerId` (read from sessionStorage once on mount)
         * and `symmetricOpponents` (a user-configured display preference that
         * should survive a game reset).
         * Dispatch this when the player leaves the table, plays again, or the
         * server signals a full game reset.
         */
        case "RESET_GAME":
            return {
                ...initialTrucoGameState,
                myPlayerId: state.myPlayerId,
                playerDetails: state.playerDetails,
                points: { ...initialTrucoGameState.points, playingPoints: 1 },
                seats: state.seats.map(s => ({
                    ...s,
                    points: 0,
                    cards: [],
                    trickWins: emptyTrickWins()
                })),
                // Preserve user preferences across game resets
                settings: state.settings,
            }
        case "RESET_HAND_STATE":
            return {
                ...state,
                ...getInitialHandState(),
                // Preserve cumulative match points; reset cards, trickWins, and playingPoints
                points: { ...state.points, playingPoints: 1 },
                seats: state.seats.map(s => ({
                    ...s,
                    cards: [],
                    trickWins: emptyTrickWins()
                }))
            }

        // ── table ────────────────────────────────────────────────────────────
        case "SET_TABLE":
            return { ...state, table: action.payload }

        case "SET_SEATS":
            // Ensure every seat always carries `points` and `trickWins`
            return {
                ...state,
                seats: action.payload.map(seat => ({
                    points: 0,
                    trickWins: emptyTrickWins(),
                    ...seat,
                }))
            }

        case "SET_DEALER":
            return { ...state, dealerIndex: action.payload }
        case "SET_HAND_INDEX":
            return { ...state, handIndex: action.payload }

        case "SET_TRICK":
            return { ...state, trickCount: action.payload }

        case "SET_PLAYER_DETAILS":
            return { ...state, playerDetails: action.payload }

        // ── cards ────────────────────────────────────────────────────────────
        case "ADD_CARD_TO_SEAT": {
            const { seatIndex, card, cardIndex, isMySeat } = action.payload
            return {
                ...state,
                seats: state.seats.map((seat, index) => {
                    if (index !== seatIndex) return seat
                    const newCard = {
                        cardValue: card,
                        cardIndex,
                        id: isMySeat
                            ? `${seatIndex}-${card}`
                            : `${seatIndex}-hidden-${cardIndex}`,
                    }
                    return { ...seat, cards: [...seat.cards, newCard] }
                }),
            }
        }

        case "REORDER_HAND":
            return {
                ...state,
                seats: state.seats.map((seat, i) =>
                    i === action.payload.seatIndex
                        ? { ...seat, cards: action.payload.newOrder }
                        : seat
                )
            }

        case "SET_CENTER_CARDS": {
            const { seatIndex, cardValue } = action.payload
            const updated = [...state.centerCards]
            while (updated.length <= seatIndex) updated.push(null)
            updated[seatIndex] = cardValue
            return { ...state, centerCards: updated }
        }

        case "MOVE_CARD_TO_CENTER": {
            const { seatIndex, cardId, cardValue, trickCount } = action.payload
            const updatedSeats = state.seats.map((seat, i) =>
                i === seatIndex
                    ? { ...seat, cards: seat.cards.filter(c => c.id !== cardId) }
                    : seat
            )
            return {
                ...state,
                seats: updatedSeats,
                centerCards: [...state.centerCards, { cardValue, seatIndex, trickCount }]
            }
        }

        // ── score ────────────────────────────────────────────────────────────
        case "SET_POINTS":
            return { ...state, points: action.payload }

        /**
         * UPDATE_POINTS
         * payload: { myPoints, opponentPoints }
         *
         * Syncs the top-level `points` object AND updates every seat's individual
         * `points` field by matching on `playerId` — safe regardless of seat
         * rotation order.
         */
        case "UPDATE_POINTS": {
            const { myPoints, opponentPoints } = action.payload
            return {
                ...state,
                points: { ...state.points, ...action.payload },
                seats: state.seats.map(seat => ({
                    ...seat,
                    points: seat.isMe
                        ? (myPoints ?? seat.points)
                        : (opponentPoints ?? seat.points)
                }))
            }
        }

        /**
         * UPDATE_TRICK_WIN
         * payload: { winnerPlayerId: string | null, trickIndex: number (0-based) }
         *
         * winnerPlayerId === null  →  draw (all seats get "draw")
         * otherwise               →  winner gets "win", all others get "lose"
         *
         * Called after every handleTrickWinner and handleTrickDraw in TableCanvas.
         */
        case "UPDATE_TRICK_WIN": {
            const { winnerPlayerId, trickIndex } = action.payload
            return {
                ...state,
                seats: state.seats.map(seat => {
                    const wins = [...(seat.trickWins ?? emptyTrickWins())]
                    if (winnerPlayerId === null) {
                        wins[trickIndex] = "draw"
                    } else {
                        wins[trickIndex] = seat.playerId === winnerPlayerId ? "win" : "lose"
                    }
                    return { ...seat, trickWins: wins }
                })
            }
        }

        // ── symmetric opponents ───────────────────────────────────────────────
        /**
         * SET_SYMMETRIC_OPPONENTS
         * payload: boolean
         * Toggles the configurable scaleX(-1) mirror on non-local seats.
         */
        case "SET_SYMMETRIC_OPPONENTS":
            return { ...state, symmetricOpponents: Boolean(action.payload) }

        // ── turn manager ─────────────────────────────────────────────────────
        case "SET_TURN":
            return {
                ...state,
                turn: { ...state.turn, currentSeat: action.payload },
                timer: { active: false }
            }

        case "SET_DEALER_SEAT":
            return {
                ...state,
                turn: { ...state.turn, dealerSeat: action.payload }
            }

        case "NEXT_TURN": {
            const totalSeats = state.seats.length
            const next = state.turn.currentSeat === totalSeats
                ? 1
                : state.turn.currentSeat + 1
            return { ...state, turn: { ...state.turn, currentSeat: next } }
        }

        // ── player actions ───────────────────────────────────────────────────
        case "SET_PLAYER_ACTIONS":
            return {
                ...state,
                playerActions: action.payload?.actions ?? [],
                action: {
                    ...state.action,
                    serverCanDraw: action.payload?.drawCard ?? state.action.serverCanDraw
                }
            }

        case "START_CARD_PLAY":
            return {
                ...state,
                action: { phase: "CARD_PLAY", canPlayCard: true, serverCanDraw: state.action.serverCanDraw }
            }

        case "START_BETTING":
            return {
                ...state,
                action: { phase: "BETTING", canPlayCard: false, serverCanDraw: state.action.serverCanDraw }
            }

        case "BLOCK_CARD_PLAY":
            return {
                ...state,
                action: { ...state.action, canPlayCard: false }
            }

        // ── truco betting ─────────────────────────────────────────────────────
        case "CALL_TRUCO":
            return {
                ...state,
                action: { phase: "BETTING", canPlayCard: false, serverCanDraw: state.action.serverCanDraw },
                bet: {
                    ...state.bet,
                    phase: "TRUCO",
                    trucoLevel: state.bet.trucoLevel + 1,
                    pendingFrom: action.payload?.seat ?? null
                }
            }

        case "RAISE_TRUCO":
            return {
                ...state,
                action: { phase: "BETTING", canPlayCard: false, serverCanDraw: state.action.serverCanDraw },
                bet: {
                    ...state.bet,
                    trucoLevel: getNextTrucoLevel(state.bet.trucoLevel),
                    pendingFrom: action.payload?.seat ?? null
                }
            }

        case "ACCEPT_BET": {
            // Truco point values: trucoLevel 1=Truco(2pts), 2=Re-Truco(3pts), 3=Cuatro(4pts)
            // Only update playingPoints when we are in the TRUCO bet phase (not Envido)
            const isTrucoBet = state.bet.phase === "TRUCO"
            const newPlayingPoints = isTrucoBet
                ? Math.min(state.bet.trucoLevel + 1, 4)
                : state.points.playingPoints
            return {
                ...state,
                action: { phase: "CARD_PLAY", canPlayCard: true, serverCanDraw: state.action.serverCanDraw },
                bet: { ...state.bet, pendingFrom: null },
                points: { ...state.points, playingPoints: newPlayingPoints }
            }
        }

        case "REJECT_BET":
            return {
                ...state,
                action: { phase: "CARD_PLAY", canPlayCard: false, serverCanDraw: state.action.serverCanDraw },
                bet: getInitialBetState()
            }

        case "PASS":
            return {
                ...state,
                action: { phase: "CARD_PLAY", canPlayCard: false, serverCanDraw: state.action.serverCanDraw },
                bet: getInitialBetState()
            }

        // ── envido flow ───────────────────────────────────────────────────────
        case "CALL_ENVIDO":
            return {
                ...state,
                action: { phase: "BETTING", canPlayCard: false, serverCanDraw: state.action.serverCanDraw },
                bet: {
                    ...state.bet,
                    phase: "ENVIDO",
                    envidoLevel: state.bet.envidoLevel + 1,
                    pendingFrom: action.payload.seat,
                    envidoChain: [...state.bet.envidoChain, action.payload.type]
                }
            }

        case "RAISE_ENVIDO":
            return {
                ...state,
                action: { phase: "BETTING", canPlayCard: false, serverCanDraw: state.action.serverCanDraw },
                bet: {
                    ...state.bet,
                    envidoLevel: state.bet.envidoLevel + 1,
                    pendingFrom: action.payload?.seat ?? null
                }
            }

        case "ACCEPT_ENVIDO":
            return {
                ...state,
                action: { phase: "CARD_PLAY", canPlayCard: true, serverCanDraw: state.action.serverCanDraw },
                bet: { ...state.bet, pendingFrom: null }
            }

        case "REJECT_ENVIDO":
            return {
                ...state,
                action: { phase: "CARD_PLAY", canPlayCard: true, serverCanDraw: state.action.serverCanDraw },
                bet: getInitialBetState()
            }

        // ── timer ─────────────────────────────────────────────────────────────
        case "START_TIMER":
            return {
                ...state,
                timer: {
                    active: true,
                    seatNumber: action.payload.seatId,
                    duration: action.payload.time ?? 30,
                    remaining: action.payload.time,
                    startedAt: Date.now()
                }
            }

        case "STOP_TIMER":
            return {
                ...state,
                timer: { active: false, seatNumber: null, duration: 0, remaining: 0, startedAt: null }
            }

        case "TICK_TIMER":
            return { ...state, timer: { ...state.timer, remaining: action.payload } }

        // ── pop-ups ───────────────────────────────────────────────────────────
        case "SET_ENVIDO_DETAILS":
            return {
                ...state,
                envidoDetails: action.payload,
                showEnvidoDetails: Boolean(action.payload)
            }

        case "SET_GAME_WIN_DETAILS":
            return {
                ...state,
                gameWinnerDetails: action.payload,
                showGameWinDetails: Boolean(action.payload),
                showRestartGameOptions: false
            }
        case "SET_SHOW_GAME_RESTART":
            return {
                ...state,
                showRestartGameOptions: true
            }

        // ── settings ──────────────────────────────────────────────────────────
        /**
         * SET_SETTINGS_OPEN
         * payload: boolean — opens or closes the settings dialog
         */
        case "SET_SETTINGS_OPEN":
            return {
                ...state,
                settings: { ...state.settings, isSettingsOpen: Boolean(action.payload) }
            }
        /**
         * SET_NOTIF_POSITION
         * payload: string — one of the POSITION_STYLES keys
         *   e.g. "bottom-center", "top-left", "center", etc.
         */
        case "SET_NOTIF_POSITION":
            return {
                ...state,
                settings: { ...state.settings, notifPosition: action.payload }
            }
        /**
         * SET_ACTIVE_THEME
         * payload: string — one of the THEMES keys
         *   e.g. "crimson", "emerald", "midnight", "obsidian"
         */
        case "SET_ACTIVE_THEME":
            return {
                ...state,
                settings: { ...state.settings, activeTheme: action.payload }
            }
        default:
            return state
    }
}